import React, { useState, useEffect } from 'react';
import { TrainerBlockForm } from '../components/TrainerBlockForm';
import { AcademyJuniorChips } from '../components/AcademyJuniorChips';
import { AdultAcademyPanel } from '../components/AdultAcademyPanel';
import { GroupTrainingParticipants } from '../components/GroupTrainingParticipants';
import { useAuth } from '../App';
import { useSearchParams } from 'react-router-dom';
import { format, startOfWeek, addDays, addWeeks, parseISO, isValid as isValidDate } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { CaretLeft, CaretRight, Plus, User, Barbell, Warning, PencilSimple, Trash, Clock, X, ArrowsClockwise, Student, CalendarBlank, NotePencil } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { AboSessionBadges } from '../components/AboSessionBadges';
import { LessonClientsEditor } from '../components/LessonClientsEditor';

const parseWeekParam = (s) => {
  try {
    const d = parseISO(s);
    if (!isValidDate(d)) return null;
    return startOfWeek(d, { weekStartsOn: 1 });
  } catch (_) { return null; }
};

const TrainerSchedule = () => {
  const { user, language, API } = useAuth();
  const dateLocale = language === 'de' ? de : enUS;
  const L = (en, deu) => language === 'de' ? deu : en;

  // Read ?week=YYYY-MM-DD from URL on first render so refresh keeps the view.
  const initialWeek = (() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('week');
      const parsed = raw ? parseWeekParam(raw) : null;
      if (parsed) return parsed;
    } catch (_) { /* ignore */ }
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  })();
  const [weekStart, setWeekStart] = useState(initialWeek);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const iso = format(weekStart, 'yyyy-MM-dd');
    if (searchParams.get('week') !== iso) {
      const next = new URLSearchParams(searchParams);
      next.set('week', iso);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);
  const [trainers, setTrainers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Customer search
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientSearching, setClientSearching] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Juniors & Recurring
  const [juniors, setJuniors] = useState([]);
  const [selectedJuniors, setSelectedJuniors] = useState([]);
  const [juniorSearch, setJuniorSearch] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringEndDate, setRecurringEndDate] = useState('');

  // Junior notes editor (mini dialog opened from the junior selector)
  const [noteJunior, setNoteJunior] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  const openJuniorNotes = (j) => {
    setNoteJunior(j);
    // Skip legacy alias where notes == training_times (schedule-like)
    const isLegacy = j.notes && j.notes === j.training_times && /[0-9]{1,2}:[0-9]{2}/.test(j.notes);
    setNoteDraft(isLegacy ? '' : (j.notes || ''));
  };
  const saveJuniorNotes = async () => {
    if (!noteJunior) return;
    setNoteSaving(true);
    try {
      await API.put(`/academy/juniors/${noteJunior.id}/notes`, { notes: noteDraft });
      toast.success(L('Notes saved', 'Notizen gespeichert'));
      // Update local list so the indicator refreshes without a full reload
      setJuniors(prev => prev.map(j => j.id === noteJunior.id ? { ...j, notes: noteDraft || null } : j));
      setNoteJunior(null);
    } catch (e) {
      toast.error(L('Save failed', 'Speichern fehlgeschlagen'));
    } finally { setNoteSaving(false); }
  };

  // Auto-price from padelarena.ch/preise matrix
  const [autoPrice, setAutoPrice] = useState(null); // {price_per_person, total_price, tier}
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceOverride, setPriceOverride] = useState(false); // show manual input when true

  // Booking modal
  const [showModal, setShowModal] = useState(false);
  const [blockMode, setBlockMode] = useState(false);
  const canBlockTime = user?.role === 'admin' || !!user?.can_block_trainer_time;
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ client_id: '', client_ids: [], selected_clients: [], lesson_type: 'training', trainer_id: '', duration: '60', price: '', voucher_id: '', subscription_id: '', subscription_assignments: {}, voucher_assignments: {}, notes: '' });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [clientVouchers, setClientVouchers] = useState([]);
  const [clientVoucherMap, setClientVoucherMap] = useState({});
  const [clientSubscriptions, setClientSubscriptions] = useState({}); // { [clientId]: [subs] }

  // Drag & drop a lesson tile to another slot (same or different day/trainer).
  // Mirrors the AdminBookings pattern: track the picked-up booking + the slot
  // under the cursor; the drop handler calls `PUT /bookings/{id}` which checks
  // conflicts + opening hours on the backend before persisting.
  const [draggedLesson, setDraggedLesson] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { trainerId, dateStr, time }

  // Detail / Edit lesson
  const [showDetail, setShowDetail] = useState(false);
  const [detailLesson, setDetailLesson] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editPriceLoading, setEditPriceLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Generate time slots from 07:00 to 23:00
  const timeSlots = [];
  for (let h = 7; h < 23; h++) {
    for (let m = 0; m < 60; m += 30) {
      timeSlots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }

  // 7 days of the week
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  useEffect(() => {
    loadTrainers();
    loadJuniors();
  }, []);

  useEffect(() => {
    loadSchedule();
  }, [weekStart]);

  useEffect(() => {
    const clients = form.selected_clients || [];
    const firstClient = clients[0];
    if (firstClient?.id) {
      loadClientVouchers(firstClient.id);
    } else {
      setClientVouchers([]);
      setForm(f => ({ ...f, voucher_id: '' }));
    }
    // Load abos for EACH selected client (need them all so admin can pick
    // per-person which abo pays for whom). Also purge any assignments
    // pointing to clients that were just removed.
    if (clients.length === 0) {
      setClientSubscriptions({});
      setClientVoucherMap({});
      setForm(f => ({ ...f, subscription_id: '', subscription_assignments: {}, voucher_assignments: {} }));
    } else {
      (async () => {
        const perClient = {};
        const perClientV = {};
        for (const c of clients) {
          try {
            const { data: vs } = await API.get(`/vouchers/user/${c.id}`);
            perClientV[c.id] = vs || [];
          } catch (e) { perClientV[c.id] = []; }
          try {
            const { data } = await API.get(`/subscriptions?user_id=${c.id}`);
            perClient[c.id] = (data || []).filter(s =>
              s.type === 'training' && Number(s.balance_remaining || 0) > 0
            );
          } catch (e) { perClient[c.id] = []; }
        }
        setClientSubscriptions(perClient);
        setClientVoucherMap(perClientV);
        // Drop assignments whose client is gone
        setForm(f => {
          const allowed = new Set(clients.map(c => c.id));
          const keep = (m) => Object.fromEntries(Object.entries(m || {}).filter(([cid, id]) => allowed.has(cid) && id));
          return { ...f, subscription_assignments: keep(f.subscription_assignments), voucher_assignments: keep(f.voucher_assignments) };
        });
      })();
    }
     
  }, [form.selected_clients]);

  // Auto-fetch the official training price whenever the slot/duration/participants changes.
  useEffect(() => {
    if (!showModal || !selectedSlot || priceOverride) return;
    // Junior trainings are always free (Abo) — skip the matrix lookup entirely.
    if (form.lesson_type === 'junior_training') {
      setAutoPrice(null);
      setPriceLoading(false);
      return;
    }
    const fetchPrice = async () => {
      try {
        setPriceLoading(true);
        const dateStr = format(selectedSlot.date, 'yyyy-MM-dd');
        const numClients = Math.max(1, form.selected_clients?.length || 0);
        // Adult Academy: 799 CHF off-peak / 999 CHF peak per person
        if (form.lesson_type === 'adult_academy') {
          const [h] = selectedSlot.time.split(':').map(Number);
          const weekday = selectedSlot.date.getDay(); // 0=Sunday..6=Saturday
          const isPeak = h >= 17 || weekday === 0 || weekday === 6;
          const six = form.academy_package === '6';
          const pricePerPerson = six ? (isPeak ? 539 : 439) : (isPeak ? 999 : 799);
          setAutoPrice({
            price_per_person: pricePerPerson,
            total_price: pricePerPerson * numClients,
            tier: isPeak ? 'peak' : 'off_peak',
            duration_minutes: parseInt(form.duration),
            participants: numClients,
          });
          setForm(f => ({ ...f, price: String(pricePerPerson * numClients) }));
          setPriceLoading(false);
          return;
        }
        const { data } = await API.get(
          `/pricing/training?date=${dateStr}&start_time=${selectedSlot.time}&duration_minutes=${parseInt(form.duration)}&participants=${numClients}`
        );
        setAutoPrice(data);
        // Keep form.price in sync (server will recompute anyway, but handy for voucher UI)
        setForm(f => ({ ...f, price: String(data.total_price) }));
      } catch (e) {
        // Matrix doesn't have this combo — auto-flip to manual override so the
        // trainer can type a price without any friction.
        setAutoPrice(null);
        setPriceOverride(true);
        setForm(f => ({ ...f, price: f.price || '100' }));
      } finally {
        setPriceLoading(false);
      }
    };
    fetchPrice();
     
  }, [showModal, selectedSlot, form.duration, form.participants, form.lesson_type, form.academy_package, form.selected_clients, priceOverride]);


  const loadTrainers = async () => {
    try {
      const { data } = await API.get('/trainers');
      // Custom club-specific display order: Fabrizio → Alejandro → Antoine.
      // Any other active trainers keep alphabetical order behind them.
      const PRIORITY = ['fabrizio', 'alejandro', 'antoine'];
      const rank = (t) => {
        const n = (t.name || '').toLowerCase();
        const idx = PRIORITY.findIndex(p => n.includes(p));
        return idx === -1 ? 999 : idx;
      };
      const sorted = data
        .filter(t => t.is_active)
        .sort((a, b) => {
          const r = rank(a) - rank(b);
          return r !== 0 ? r : (a.name || '').localeCompare(b.name || '', 'de', { sensitivity: 'base' });
        });
      setTrainers(sorted);
    } catch (e) { console.error(e); }
  };
  const loadJuniors = async () => {
    try { const { data } = await API.get('/academy/juniors'); setJuniors(data); } catch (e) { console.error(e); }
  };

  // Debounced client search
  useEffect(() => {
    if (clientSearch.length < 2) { setClientResults([]); return; }
    const timer = setTimeout(async () => {
      setClientSearching(true);
      try {
        const { data } = await API.get(`/users/search?q=${encodeURIComponent(clientSearch)}&limit=20`);
        setClientResults(data);
        setShowClientDropdown(true);
      } catch (e) { console.error(e); }
      finally { setClientSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);
  const loadSchedule = async () => {
    try {
      setLoading(true);
      const dateStr = format(weekStart, 'yyyy-MM-dd');
      const { data } = await API.get(`/trainer/schedule?date=${dateStr}`);
      setBookings(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  const loadClientVouchers = async (userId) => {
    try { const { data } = await API.get(`/vouchers/user/${userId}`); setClientVouchers(data); }
    catch (e) { setClientVouchers([]); }
  };

  // Get top 3 active trainers
  const displayTrainers = trainers.slice(0, 3);

  // Sum booked minutes per (trainer × day) for the footer/summary card.
  //   dayHours[trainerId][dateStr] = minutes
  //   totalHoursByTrainer[trainerId] = week total in minutes
  //   grandTotalMinutes = week total across all trainers
  const { dayHours, totalHoursByTrainer, grandTotalMinutes } = React.useMemo(() => {
    const dayHours = {};
    const totalHoursByTrainer = {};
    let grand = 0;
    const weekDateStrs = weekDays.map(d => format(d, 'yyyy-MM-dd'));
    for (const tr of displayTrainers) {
      dayHours[tr.id] = {};
      totalHoursByTrainer[tr.id] = 0;
      for (const ds of weekDateStrs) dayHours[tr.id][ds] = 0;
    }
    // Only real, confirmed lessons count: no cancelled / unpaid-pending
    // checkouts, no empty group sessions, and overlapping duplicates of the
    // same trainer slot are merged so a time window is never counted twice.
    const toMin = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return h * 60 + (m || 0); };
    const intervals = {};
    for (const b of bookings) {
      if (!b.trainer_id || !b.date || !b.start_time) continue;
      if (['cancelled', 'pending', 'expired', 'failed', 'rejected'].includes(b.status)) continue;
      if (b.booking_type === 'group_training' && !(b.participant_count > 0)) continue;
      if (b.booking_type === 'trainer_block') continue;
      if (!(b.trainer_id in dayHours) || !(b.date in dayHours[b.trainer_id])) continue;
      const s = toMin(b.start_time);
      let e = b.end_time ? toMin(b.end_time) : s + Number(b.duration_minutes || 0);
      if (e <= s) e = s + Number(b.duration_minutes || 0);
      const key = `${b.trainer_id}|${b.date}`;
      (intervals[key] = intervals[key] || []).push([s, e]);
    }
    for (const [key, list] of Object.entries(intervals)) {
      const [tid, ds] = key.split('|');
      list.sort((a, c) => a[0] - c[0]);
      let mins = 0, curS = null, curE = null;
      for (const [s, e] of list) {
        if (curE === null || s >= curE) { if (curE !== null) mins += curE - curS; curS = s; curE = e; }
        else curE = Math.max(curE, e);
      }
      if (curE !== null) mins += curE - curS;
      dayHours[tid][ds] += mins;
      totalHoursByTrainer[tid] += mins;
      grand += mins;
    }
    return { dayHours, totalHoursByTrainer, grandTotalMinutes: grand };
  }, [bookings, weekDays, displayTrainers]);

  const fmtH = (mins) => (mins / 60).toFixed(1);

  // Series-position map: for every recurring lesson, compute 1-based position
  // (1st, 2nd, 3rd…) by sorting all live bookings of the same
  // `recurring_group_id` by (date, start_time). When a session gets cancelled,
  // the remaining ones automatically slide up (4th becomes 3rd etc.) because
  // we only count non-cancelled rows. Shown as a tiny badge bottom-right.
  const seriesPositionByBookingId = React.useMemo(() => {
    const groups = {};
    for (const b of bookings) {
      const gid = b.recurring_group_id || b.recurring_id;
      if (!gid || b.status === 'cancelled') continue;
      if (!groups[gid]) groups[gid] = [];
      groups[gid].push(b);
    }
    const out = {};
    for (const gid in groups) {
      const sorted = [...groups[gid]].sort((a, b) =>
        (a.date || '').localeCompare(b.date || '')
        || (a.start_time || '').localeCompare(b.start_time || '')
      );
      const total = sorted.length;
      // Only show numbering when there are ≥2 live sessions — a single
      // orphaned session doesn't need a "1/1" badge.
      if (total < 2) continue;
      sorted.forEach((b, idx) => { out[b.id] = { pos: idx + 1, total }; });
    }
    return out;
  }, [bookings]);

  // Move a lesson via drag-and-drop. Reuses the standard booking PUT
  // endpoint which already enforces conflict + opening-hours rules.
  const moveLessonToSlot = async (lesson, targetTrainerId, targetDate, targetTime) => {
    if (!lesson) return;
    const sameSpot = lesson.trainer_id === targetTrainerId
                     && lesson.date === targetDate
                     && lesson.start_time === targetTime;
    if (sameSpot) return;
    try {
      await API.put(`/bookings/${lesson.id}`, {
        trainer_id: targetTrainerId,
        date: targetDate,
        start_time: targetTime,
      });
      toast.success(L('Lesson moved', 'Lektion verschoben'));
      await loadSchedule();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Move failed', 'Verschieben fehlgeschlagen'));
    }
  };

  // Check if a trainer has a booking at a given time on a given date
  const getBookingAt = (trainerId, date, time) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookings.find(b => {
      if (b.trainer_id !== trainerId || b.date !== dateStr) return false;
      if (!b.start_time) return false;
      const startMin = parseInt(b.start_time.split(':')[0]) * 60 + parseInt(b.start_time.split(':')[1]);
      // Prefer end_time if present, otherwise derive from duration_minutes
      let endMin;
      if (b.end_time && b.end_time.includes(':')) {
        endMin = parseInt(b.end_time.split(':')[0]) * 60 + parseInt(b.end_time.split(':')[1]);
      } else {
        endMin = startMin + (parseInt(b.duration_minutes) || 60);
      }
      const slotMin = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
      return slotMin >= startMin && slotMin < endMin;
    });
  };

  const isBookingStart = (trainerId, date, time) => {
    const booking = getBookingAt(trainerId, date, time);
    return booking?.start_time === time ? booking : null;
  };

  const handleSlotClick = (trainer, date, time) => {
    if (getBookingAt(trainer.id, date, time)) return;
    setSelectedSlot({ trainer, date, time });
    setForm({ client_id: '', client_ids: [], selected_clients: [], lesson_type: 'training', trainer_id: trainer.id, duration: '60', participants: '1', price: '', voucher_id: '', subscription_id: '', subscription_assignments: {}, voucher_assignments: {}, notes: '' });
    setAutoPrice(null);
    setPriceOverride(false);
    setClientSearch('');
    setSelectedClientName('');
    setClientResults([]);
    setShowClientDropdown(false);
    setSelectedJuniors([]);
    setJuniorSearch('');
    setIsRecurring(false);
    setRecurringEndDate('');
    setBlockMode(false);
    setShowModal(true);
  };

  const handleBook = async () => {
    if (!selectedSlot) return;
    const isJuniorTraining = form.lesson_type === 'junior_training';
    const clients = form.selected_clients || [];
    if (isJuniorTraining) {
      if (selectedJuniors.length === 0) {
        toast.error(L('Pick at least one junior', 'Bitte mindestens einen Junior auswählen'));
        return;
      }
    } else {
      if (clients.length === 0) return;
      // If matrix has a price, we're good. If override is on, require a manual price.
      if (priceOverride && !form.price) return;
      if (!priceOverride && !autoPrice) return;
    }
    setBookingLoading(true);
    try {
      const payload = {
        trainer_id: form.trainer_id,
        client_ids: isJuniorTraining ? [] : clients.map(c => c.id),
        lesson_type: form.lesson_type || 'training',
        academy_package: form.lesson_type === 'adult_academy' ? (form.academy_package || '10') : undefined,
        date: format(selectedSlot.date, 'yyyy-MM-dd'),
        start_time: selectedSlot.time,
        duration_minutes: parseInt(form.duration),
        participants: isJuniorTraining ? Math.max(1, selectedJuniors.length) : clients.length,
        notes: form.notes || null,
        junior_ids: selectedJuniors,
      };
      // Only send manual price if the trainer actively overrode it; otherwise
      // the backend uses the official matrix automatically. Junior trainings
      // are always free (Abo) — never send a price.
      if (!isJuniorTraining && priceOverride && form.price) {
        payload.lesson_price = parseFloat(form.price);
      }
      if (!isJuniorTraining) {
        const va = Object.entries(form.voucher_assignments || {}).filter(([, vid]) => vid).map(([cid, vid]) => ({ client_id: cid, voucher_id: vid }));
        if (va.length) payload.voucher_assignments = va;
      }
      // Abo assignments: one entry per client that picked an abo
      if (!isJuniorTraining) {
        const assignmentList = Object.entries(form.subscription_assignments || {})
          .filter(([_cid, sid]) => sid)
          .map(([cid, sid]) => ({ client_id: cid, subscription_id: sid }));
        if (assignmentList.length > 0) {
          payload.subscription_assignments = assignmentList;
        }
      }
      if (isRecurring && recurringEndDate) payload.recurring_end_date = recurringEndDate;
      
      const { data } = await API.post('/trainer/book-lesson', payload);
      if (data.booked !== undefined) {
        // Recurring series response — surface conflicts as their own message
        const conflictCount = data.conflicts?.length || 0;
        const conflictDates = (data.conflicts || []).slice(0, 5).join(', ');
        const moreSuffix = conflictCount > 5 ? ` (+${conflictCount - 5})` : '';
        if (data.booked === 0) {
          // Every single date collided — pure error state
          toast.error(
            `${L('No lesson booked — all dates conflict', 'Keine Lektion gebucht – alle Termine sind belegt')}` +
            (conflictCount > 0 ? `: ${conflictDates}${moreSuffix}` : ''),
            { duration: 8000 }
          );
        } else if (conflictCount > 0) {
          toast.warning(
            `${data.booked} ${L('lessons booked', 'Lektionen gebucht')} · ${conflictCount} ${L('conflict(s)', 'Konflikt(e)')}: ${conflictDates}${moreSuffix}`,
            { duration: 8000 }
          );
        } else {
          toast.success(`${data.booked} ${L('lessons booked', 'Lektionen gebucht')}`);
        }
      } else {
        toast.success(`${L('Lesson booked!', 'Lektion gebucht!')} → ${data.court_name}`);
      }
      setShowModal(false);
      // If the new booking landed in a different ISO week, jump the calendar
      // there so the coach can immediately see it — no manual refresh needed.
      if (data?.date) {
        const bookingDate = parseISO(data.date);
        if (isValidDate(bookingDate)) {
          const bookingWeek = startOfWeek(bookingDate, { weekStartsOn: 1 });
          if (format(bookingWeek, 'yyyy-MM-dd') !== format(weekStart, 'yyyy-MM-dd')) {
            setWeekStart(bookingWeek);
            return; // useEffect on weekStart triggers loadSchedule
          }
        }
      }
      await loadSchedule();
    } catch (error) {
      const msg = error.response?.data?.detail || L('Booking failed', 'Buchung fehlgeschlagen');
      if (msg.includes('No courts')) {
        toast.error(L('No courts available at this time!', 'Keine Plätze zu dieser Zeit verfügbar!'));
      } else {
        toast.error(msg);
      }
    } finally { setBookingLoading(false); }
  };

  const navigateWeek = (dir) => setWeekStart(prev => addWeeks(prev, dir));

  // Click on an existing lesson
  const handleLessonClick = (booking) => {
    setDetailLesson(booking);
    setIsEditing(false);
    setShowDeleteConfirm(false);
    setShowDetail(true);
  };

  // Start editing a lesson
  const startEditLesson = () => {
    const b = detailLesson;
    const ids = b.client_ids || (b.user_id ? [b.user_id] : []);
    const names = b.client_names || (b.user_name ? [b.user_name] : []);
    setEditForm({
      trainer_id: b.trainer_id || '',
      start_time: b.start_time,
      duration_minutes: String(b.duration_minutes),
      lesson_price: String(b.amount || 100),
      notes: b.notes || '',
      clients: ids.map((id, i) => ({ id, name: names[i] || '' })),
    });
    setIsEditing(true);
  };

  const editClientsChanged = (() => {
    const b = detailLesson;
    if (!b || !editForm.clients) return false;
    const orig = (b.client_ids || (b.user_id ? [b.user_id] : [])).join(',');
    return orig !== editForm.clients.map(c => c.id).join(',');
  })();
  const canEditClients = !!detailLesson && (detailLesson.lesson_type || 'training') === 'training'
    && detailLesson.booking_type === 'training' && !detailLesson.academy_category;

  // Live price for the new player count / duration
  useEffect(() => {
    const b = detailLesson;
    if (!isEditing || !b || !editForm.clients) return;
    const durChanged = parseInt(editForm.duration_minutes) !== b.duration_minutes;
    if (!editClientsChanged && !durChanged) return;
    const n = Math.max(1, Math.min(4, editForm.clients.length));
    setEditPriceLoading(true);
    API.get(`/pricing/training?date=${b.date}&start_time=${editForm.start_time}&duration_minutes=${parseInt(editForm.duration_minutes)}&participants=${n}`)
      .then(({ data }) => setEditForm(f => ({ ...f, lesson_price: String(data.total_price) })))
      .catch(() => {})
      .finally(() => setEditPriceLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, editClientsChanged, editForm.clients?.length, editForm.duration_minutes, editForm.start_time]);

  // Save lesson edits
  const handleSaveLesson = async () => {
    if (!detailLesson) return;
    setEditLoading(true);
    try {
      const payload = {};
      const b = detailLesson;
      let updated = null;
      if (canEditClients && editClientsChanged) {
        const { data } = await API.put(`/trainer/lessons/${b.id}/clients`, {
          client_ids: editForm.clients.map(c => c.id),
          lesson_price: parseFloat(editForm.lesson_price),
        });
        updated = data;
        if (data.refunded_amount > 0) {
          toast.info(L(`Refund CHF ${data.refunded_amount.toFixed(2)} booked in the Kasse`, `Rückerstattung CHF ${data.refunded_amount.toFixed(2)} in der Kasse verbucht`));
        }
      }
      if (editForm.trainer_id !== (b.trainer_id || '')) payload.trainer_id = editForm.trainer_id;
      if (editForm.start_time !== b.start_time) payload.start_time = editForm.start_time;
      if (parseInt(editForm.duration_minutes) !== b.duration_minutes) payload.duration_minutes = parseInt(editForm.duration_minutes);
      if (!updated && parseFloat(editForm.lesson_price) !== b.amount) payload.lesson_price = parseFloat(editForm.lesson_price);
      if (editForm.notes !== (b.notes || '')) payload.notes = editForm.notes;

      if (Object.keys(payload).length === 0 && !updated) {
        toast.info(L('No changes', 'Keine Änderungen'));
        setIsEditing(false);
        setEditLoading(false);
        return;
      }

      if (Object.keys(payload).length > 0) {
        const { data } = await API.put(`/bookings/${detailLesson.id}`, payload);
        updated = data;
      }
      const data = updated;
      toast.success(L('Lesson updated!', 'Lektion aktualisiert!'));
      setDetailLesson(data);
      setIsEditing(false);
      loadSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || L('Update failed', 'Aktualisierung fehlgeschlagen'));
      if (error.response?.status === 404) {
        setShowDetail(false);
        loadSchedule();
      }
    } finally { setEditLoading(false); }
  };

  // Delete lesson with voucher refund
  const handleDeleteLesson = async () => {
    if (!detailLesson) return;
    try {
      await API.delete(`/bookings/${detailLesson.id}`);
      toast.success(detailLesson.voucher_id
        ? L('Lesson deleted & voucher refunded', 'Lektion gelöscht & Gutschein erstattet')
        : L('Lesson deleted', 'Lektion gelöscht'));
    } catch (e) {
      if (e.response?.status === 404) {
        toast.info(L('Lesson was already removed', 'Lektion wurde bereits entfernt'));
      } else {
        toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
      }
    }
    setShowDeleteConfirm(false);
    setShowDetail(false);
    setDetailLesson(null);
    loadSchedule();
  };

  // Delete this lesson AND every future occurrence in the recurring series.
  const handleDeleteSeries = async () => {
    const groupId = detailLesson?.recurring_group_id || detailLesson?.recurring_id;
    if (!groupId) return;
    try {
      // Trainer recurring series live on `recurring_group_id`; court recurring
      // series use `recurring_id` and the /recurring-bookings endpoint.
      if (detailLesson.recurring_group_id) {
        const { data } = await API.delete(`/trainer/recurring-lessons/${groupId}?from_date=${detailLesson.date}`);
        toast.success(`${data.deleted} ${L('lessons deleted', 'Lektionen gelöscht')}${data.refunded ? ' · ' + L('Abos refunded', 'Abos erstattet') : ''}`);
      } else {
        await API.delete(`/recurring-bookings/${groupId}?delete_future=true`);
        toast.success(L('Whole series deleted', 'Gesamte Serie gelöscht'));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
    setShowDeleteConfirm(false);
    setShowDetail(false);
    setDetailLesson(null);
    loadSchedule();
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="trainer-schedule">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">{L('TRAINER SCHEDULE', 'TRAINERPLAN')}</h1>
          <p className="text-slate-500">{L('Weekly overview of all training lessons', 'Wochenübersicht aller Trainingslektionen')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigateWeek(-1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="prev-week">
            <CaretLeft size={20} className="text-slate-600" />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="inline-flex items-center gap-2 min-w-[200px] justify-center px-3 py-2 rounded-lg hover:bg-slate-100 text-sm font-bold text-slate-800 transition-colors"
                data-testid="week-picker-trigger"
                title={L('Jump to any week', 'Zu beliebiger Woche springen')}
              >
                <CalendarBlank size={16} className="text-slate-500" />
                {format(weekStart, 'd. MMM', { locale: dateLocale })} - {format(addDays(weekStart, 6), 'd. MMM yyyy', { locale: dateLocale })}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center">
              <Calendar
                mode="single"
                selected={weekStart}
                onSelect={(d) => { if (d) setWeekStart(startOfWeek(d, { weekStartsOn: 1 })); }}
                weekStartsOn={1}
                showOutsideDays
                locale={dateLocale}
                initialFocus
                data-testid="week-picker-calendar"
              />
              <div className="border-t border-slate-100 p-2 flex justify-between items-center">
                <button
                  onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
                  className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-md hover:bg-emerald-50"
                  data-testid="week-picker-today"
                >
                  {L('Today', 'Heute')}
                </button>
                <span className="text-[11px] text-slate-400">
                  {L('Click any day to jump to that week', 'Klicke einen Tag um zu dieser Woche zu springen')}
                </span>
              </div>
            </PopoverContent>
          </Popover>
          <button onClick={() => navigateWeek(1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="next-week">
            <CaretRight size={20} className="text-slate-600" />
          </button>
        </div>
      </div>

      {displayTrainers.length === 0 ? (
        <div className="card-static p-12 text-center">
          <Barbell size={48} className="text-slate-300 mx-auto mb-4" weight="duotone" />
          <p className="text-slate-500">{L('No active trainers. Add trainers in the Trainers settings.', 'Keine aktiven Trainer. Trainer in den Einstellungen hinzufügen.')}</p>
        </div>
      ) : (
        /* Days side-by-side, horizontally scrollable; each day is a compact column */
        <div className="overflow-x-auto pb-2" data-testid="trainer-schedule-week-scroll">
          <div className="flex gap-3 items-start min-w-max">
          {weekDays.map((day) => {
            const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
            return (
              <div key={format(day, 'yyyy-MM-dd')} className="bg-white border border-slate-200/60 rounded-2xl shadow-sm overflow-hidden w-[340px] flex-shrink-0" data-testid={`trainer-day-card-${format(day, 'yyyy-MM-dd')}`}>
                {/* Day Header */}
                <div className={`px-3 py-2 border-b ${isToday ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                  <h3 className={`text-sm font-bold ${isToday ? 'text-emerald-700' : 'text-slate-800'}`}>
                    {format(day, 'EEE, d. MMM', { locale: dateLocale })}
                    {isToday && <span className="ml-2 text-[9px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">{L('Today', 'Heute')}</span>}
                  </h3>
                </div>

                <div>
                  <div>
                    {/* Trainer Column Headers */}
                    <div className="grid border-b border-slate-200" style={{ gridTemplateColumns: `44px repeat(${displayTrainers.length}, 1fr)` }}>
                      <div className="p-1 text-center text-[9px] text-slate-400 font-semibold uppercase">{L('Time', 'Zeit')}</div>
                      {displayTrainers.map(tr => (
                        <div key={tr.id} className="p-1 text-center border-l border-slate-200">
                          <p className="text-[11px] font-bold text-slate-800 truncate" title={tr.name}>{(tr.name || '').split(' ')[0]}</p>
                        </div>
                      ))}
                    </div>

                    {/* Time slots */}
                    {timeSlots.map((time) => (
                      <div key={time} className="grid border-b border-slate-50" style={{ gridTemplateColumns: `44px repeat(${displayTrainers.length}, 1fr)` }}>
                        <div className="p-1 text-center text-[10px] font-medium text-slate-400">{time}</div>
                        {displayTrainers.map(tr => {
                          const booking = getBookingAt(tr.id, day, time);
                          const startBooking = isBookingStart(tr.id, day, time);
                          const dayStr = format(day, 'yyyy-MM-dd');
                          const isDropHover = dropTarget
                            && dropTarget.trainerId === tr.id
                            && dropTarget.dateStr === dayStr
                            && dropTarget.time === time;

                          return (
                            <div key={`${tr.id}-${time}`}
                              onClick={() => !booking && handleSlotClick(tr, day, time)}
                              onDragOver={(e) => {
                                if (draggedLesson) {
                                  e.preventDefault();
                                  setDropTarget({ trainerId: tr.id, dateStr: dayStr, time });
                                }
                              }}
                              onDragLeave={() => {
                                if (isDropHover) setDropTarget(null);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                if (draggedLesson) {
                                  moveLessonToSlot(draggedLesson, tr.id, dayStr, time);
                                  setDraggedLesson(null);
                                  setDropTarget(null);
                                }
                              }}
                              className={`relative min-h-[32px] border-l border-slate-50 transition-all ${
                                !booking ? 'hover:bg-emerald-50 cursor-pointer' : ''
                              } ${isDropHover ? 'ring-2 ring-blue-400 ring-inset bg-blue-50' : ''}`}
                              data-testid={`schedule-${tr.id}-${dayStr}-${time}`}
                            >
                              {startBooking && (() => {
                                const slotCount = startBooking.duration_minutes / 30;
                                const hasVoucher = !!startBooking.voucher_id;
                                const isPaid = startBooking.payment_status === 'paid';
                                const juniorList = [startBooking.academy_junior_names, startBooking.academy_member_names, startBooking.junior_names].find(a => a && a.length) || [];
                                const isGroup = startBooking.booking_type === 'group_training';
                                const isBlock = startBooking.booking_type === 'trainer_block';
                                const isJunior = startBooking.academy_category === 'junior' || startBooking.lesson_type === 'junior_training';
                                const isAdultAcademy = startBooking.academy_category === 'adult' || startBooking.lesson_type === 'adult_academy';
                                const darkGreen = hasVoucher || isPaid;
                                // Match the AdminBookings palette:
                                //   isGroup           → indigo-500
                                //   isJunior          → slate-400 (gray)
                                //   isAdultAcademy    → yellow-400 (Adult Academy season fee)
                                //   darkGreen (paid)  → emerald-500
                                //   else (unpaid)     → emerald-300
                                let blockStyle;
                                let lightText;   // when true, text is drawn white/light for contrast
                                if (isBlock) {
                                  blockStyle = 'bg-slate-200 border-slate-400 border-dashed bg-[repeating-linear-gradient(45deg,transparent,transparent_6px,rgba(100,116,139,0.18)_6px,rgba(100,116,139,0.18)_12px)]';
                                  lightText = false;
                                } else if (isGroup) {
                                  blockStyle = 'bg-indigo-500 border-indigo-700';
                                  lightText = true;
                                } else if (isJunior) {
                                  blockStyle = 'bg-slate-400 border-slate-600';
                                  lightText = true;
                                } else if (isAdultAcademy) {
                                  blockStyle = 'bg-yellow-400 border-yellow-600';
                                  lightText = false;   // dark text on yellow reads better
                                } else if (darkGreen) {
                                  // Training bezahlt = dunkelblau (matches AdminBookings palette Feb 2026)
                                  blockStyle = 'bg-blue-700 border-blue-900';
                                  lightText = true;
                                } else {
                                  // Training unbezahlt = dunkelgrün
                                  blockStyle = 'bg-emerald-600 border-emerald-800';
                                  lightText = true;
                                }
                                return (
                                  <div
                                    draggable={!isBlock}
                                    onDragStart={(e) => {
                                      if (isBlock) return;
                                      e.stopPropagation();
                                      e.dataTransfer.effectAllowed = 'move';
                                      e.dataTransfer.setData('text/plain', startBooking.id);
                                      setDraggedLesson(startBooking);
                                    }}
                                    onDragEnd={() => {
                                      setDraggedLesson(null);
                                      setDropTarget(null);
                                    }}
                                    className={`absolute left-0 right-0 top-0 rounded-lg p-1.5 z-10 overflow-hidden border ${isBlock ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'} hover:brightness-90 transition-all ${blockStyle} ${draggedLesson?.id === startBooking.id ? 'opacity-40' : ''}`}
                                    style={{ height: `${Math.max(slotCount, juniorList.length > 0 ? 2 : 1) * 33 - 1}px` }}
                                    onClick={(e) => { e.stopPropagation(); handleLessonClick(startBooking); }}
                                    title={isBlock ? L('Click to open', 'Klicken zum Öffnen') : L('Click to open · Drag to move', 'Klicken zum Öffnen · Ziehen zum Verschieben')}
                                    data-testid={isBlock ? `trainer-block-${startBooking.block_id}` : isGroup ? `group-training-block-${startBooking.id}` : `lesson-block-${startBooking.id}`}
                                  >
                                    {/* Series position badge (bottom-right).
                                        Only rendered when a recurring_group_id
                                        exists AND the series has ≥2 live
                                        sessions. Updates automatically when
                                        a session is cancelled (sliding renumber). */}
                                    <AboSessionBadges sessions={startBooking.abo_sessions} className="absolute bottom-0.5 left-0.5 right-6 z-20" testPrefix={`abo-session-${startBooking.id}`} />
                                    {seriesPositionByBookingId[startBooking.id] && !startBooking.abo_sessions && (
                                      <span
                                        className="absolute bottom-0.5 right-0.5 bg-black/35 text-white text-[9px] font-bold rounded-md px-1 py-[1px] leading-none pointer-events-none"
                                        title={L(`Session ${seriesPositionByBookingId[startBooking.id].pos} of ${seriesPositionByBookingId[startBooking.id].total}`,
                                                 `Lektion ${seriesPositionByBookingId[startBooking.id].pos} von ${seriesPositionByBookingId[startBooking.id].total}`)}
                                        data-testid={`series-position-${startBooking.id}`}
                                      >
                                        {seriesPositionByBookingId[startBooking.id].pos}/{seriesPositionByBookingId[startBooking.id].total}
                                      </span>
                                    )}
                                    {isBlock ? (
                                      <div className="text-slate-700 space-y-0.5">
                                        <p className="text-[10px] font-black uppercase tracking-wider">{L('Blocked', 'Blockiert')}</p>
                                        <p className="text-[9px]">{startBooking.start_time}-{startBooking.end_time}</p>
                                        <p className="text-[10px] font-semibold leading-tight break-words" title={startBooking.notes}>{startBooking.notes}</p>
                                      </div>
                                    ) : isGroup ? (
                                      <div className="text-white space-y-0.5">
                                        <p className="text-[10px] font-black uppercase tracking-wider">
                                          {L('Group', 'Gruppe')}
                                        </p>
                                        <p className="text-[10px] font-bold truncate">{startBooking.notes || L('Training', 'Training')}</p>
                                        <p className="text-[9px]">
                                          {startBooking.start_time} · {startBooking.participant_count || 0}/{startBooking.max_participants || 0}
                                        </p>
                                      </div>
                                    ) : (
                                      <>
                                        <div className="flex justify-between items-start gap-2 mb-0.5">
                                          <div className="min-w-0 flex-shrink-0">
                                            <p className={`text-[10px] font-bold truncate ${lightText ? 'text-white' : 'text-slate-900'}`}>
                                              {isJunior
                                                ? (juniorList.length > 0 ? juniorList.join(' · ') : (L('Junior', 'Junior')))
                                                : (startBooking.academy_group_id ? (startBooking.academy_group_name || 'Academy') : ((startBooking.client_names || []).length > 1 ? startBooking.client_names.join(', ') : startBooking.user_name))}
                                            </p>
                                            <p className={`text-[9px] leading-tight ${lightText ? 'text-white/85' : 'text-slate-700'}`}>
                                              {startBooking.start_time}-{startBooking.end_time}
                                              {!isJunior && ` · ${startBooking.amount} CHF`}
                                            </p>
                                          </div>
                                          {!isJunior && juniorList.length > 0 && (
                                            <p
                                              className={`text-[11px] font-semibold leading-tight text-right flex-1 min-w-0 break-words ${lightText ? 'text-white' : 'text-slate-800'}`}
                                              title={juniorList.join(', ')}
                                            >
                                              {juniorList.join(' · ')}
                                            </p>
                                          )}
                                        </div>
                                        {startBooking.notes && (
                                          <p
                                            className={`text-[10px] leading-tight break-words ${lightText ? 'text-white/95' : 'text-slate-800'}`}
                                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                            title={startBooking.notes}
                                          >
                                            {startBooking.notes}
                                          </p>
                                        )}
                                      </>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>

                  {/* Per-day footer — hours per trainer (no day-total) */}
                  <div className="border-t border-slate-200 bg-slate-50/70 px-2 py-1.5">
                    <div className="grid gap-1 items-center" style={{ gridTemplateColumns: `44px repeat(${displayTrainers.length}, 1fr)` }}>
                      <div className="text-[9px] font-semibold uppercase text-slate-400 text-center">{L('h', 'h')}</div>
                      {displayTrainers.map(tr => {
                        const dateStr = format(day, 'yyyy-MM-dd');
                        const mins = dayHours[tr.id]?.[dateStr] || 0;
                        return (
                          <div key={tr.id} className={`text-center text-[11px] font-bold ${mins > 0 ? 'text-blue-600' : 'text-slate-300'}`} data-testid={`day-hours-${tr.id}-${dateStr}`}>
                            {fmtH(mins)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Weekly summary card — same width/style as day cards */}
          <div className="bg-gradient-to-br from-blue-50 to-emerald-50 border border-blue-200 rounded-2xl shadow-sm overflow-hidden w-[340px] flex-shrink-0" data-testid="trainer-week-summary">
            <div className="px-3 py-2 border-b border-blue-200 bg-blue-100/50">
              <h3 className="text-sm font-bold text-blue-900">
                {L('Weekly total', 'Wochen-Total')}
                <span className="ml-2 text-[9px] bg-blue-500 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider">{L('Summary', 'Übersicht')}</span>
              </h3>
            </div>
            <div className="p-3 space-y-2">
              {displayTrainers.map(tr => {
                const totalMin = totalHoursByTrainer[tr.id] || 0;
                return (
                  <div key={tr.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`week-total-${tr.id}`}>
                    <span className="font-semibold text-slate-800 text-sm">{(tr.name || '').split(' ')[0]}</span>
                    <span className="text-lg font-extrabold text-blue-600 tabular-nums">{fmtH(totalMin)}<span className="text-xs text-slate-400 ml-1">h</span></span>
                  </div>
                );
              })}
              <div className="flex items-center justify-between bg-blue-600 text-white rounded-lg px-3 py-3 mt-3" data-testid="week-grand-total">
                <span className="font-bold text-sm uppercase tracking-wider">{L('Total all', 'Total alle')}</span>
                <span className="text-2xl font-black tabular-nums">{fmtH(grandTotalMinutes)}<span className="text-xs text-white/70 ml-1">h</span></span>
              </div>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md max-h-[90vh] overflow-y-auto" data-testid="trainer-booking-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{blockMode ? L('Block trainer time', 'Trainer-Zeit blockieren') : L('Book Training Lesson', 'Trainingslektion buchen')}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {selectedSlot && `${selectedSlot.trainer.name} — ${format(selectedSlot.date, 'EEEE, d. MMM', { locale: dateLocale })} ${selectedSlot.time}`}
            </DialogDescription>
          </DialogHeader>

          {selectedSlot && canBlockTime && (
            <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-lg" data-testid="slot-mode-toggle">
              <button type="button" onClick={() => setBlockMode(false)} data-testid="slot-mode-lesson"
                className={`py-1.5 rounded-md text-sm font-semibold transition-colors ${!blockMode ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>
                {L('Lesson', 'Lektion')}
              </button>
              <button type="button" onClick={() => setBlockMode(true)} data-testid="slot-mode-block"
                className={`py-1.5 rounded-md text-sm font-semibold transition-colors ${blockMode ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>
                {L('Block time', 'Zeit blockieren')}
              </button>
            </div>
          )}

          {selectedSlot && blockMode && (
            <TrainerBlockForm slot={selectedSlot} trainerId={selectedSlot.trainer.id}
              onCancel={() => setShowModal(false)}
              onDone={() => { setShowModal(false); loadSchedule(); }} />
          )}

          {selectedSlot && !blockMode && (
            <div className="space-y-4 mt-4">
              {/* Trainer — one tab per trainer, same style as Lesson Type */}
              <div className="space-y-2">
                <Label className="label-text">{L('Trainer', 'Trainer')}</Label>
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${displayTrainers.length}, minmax(0, 1fr))` }}>
                  {displayTrainers.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, trainer_id: t.id }))}
                      data-testid={`lesson-trainer-${t.id}`}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold border transition truncate ${
                        form.trainer_id === t.id
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-blue-400'
                      }`}
                      title={t.name}
                    >
                      {(t.name || '').split(' ')[0]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Lesson type */}
              <div className="space-y-2">
                <Label className="label-text">{L('Lesson Type', 'Lektions-Typ')}</Label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, lesson_type: 'training' }))}
                    data-testid="lesson-type-training"
                    className={`px-3 py-2 rounded-lg text-sm font-semibold border transition ${
                      form.lesson_type === 'training'
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'
                    }`}
                  >
                    {L('Regular', 'Regulär')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, lesson_type: 'junior_training' }))}
                    data-testid="lesson-type-junior-training"
                    className={`px-3 py-2 rounded-lg text-sm font-semibold border transition ${
                      form.lesson_type === 'junior_training'
                        ? 'bg-slate-500 text-white border-slate-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {L('Junior', 'Junior')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, lesson_type: 'adult_academy' }))}
                    data-testid="lesson-type-adult-academy"
                    className={`px-3 py-2 rounded-lg text-sm font-semibold border transition ${
                      form.lesson_type === 'adult_academy'
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-amber-400'
                    }`}
                  >
                    {L('Adult Ac.', 'Adult Ac.')}
                  </button>
                </div>
                {form.lesson_type === 'adult_academy' && (
                  <div className="space-y-2" data-testid="academy-package-picker">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { k: '10', label: L('Full course', 'Ganzer Kurs'), price: '799 / 999' },
                        { k: '6', label: L('6 sessions', '6 Sessions'), price: '439 / 539' },
                      ].map(o => {
                        const on = (form.academy_package || '10') === o.k;
                        return (
                          <button key={o.k} type="button" data-testid={`academy-package-${o.k}`}
                            onClick={() => {
                              setForm(f => ({ ...f, academy_package: o.k }));
                              if (o.k === '6' && selectedSlot?.date) {
                                const end = new Date(selectedSlot.date); end.setDate(end.getDate() + 35);
                                setRecurringEndDate(format(end, 'yyyy-MM-dd'));
                              }
                            }}
                            className={`px-3 py-2 rounded-lg border-2 text-left transition-colors ${on ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white hover:border-amber-300'}`}>
                            <p className="text-sm font-bold text-slate-900">{o.label}</p>
                            <p className="text-[11px] text-slate-500">{o.price} CHF</p>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                      {form.academy_package === '6'
                        ? L('6 sessions: 439 CHF/person before 17:00 · 539 CHF/person from 17:00 & weekends · weekly series set to 6 dates',
                            '6 Sessions: 439 CHF/Person vor 17:00 · 539 CHF/Person ab 17:00 & Wochenende · Serie auf 6 Termine gesetzt')
                        : L('799 CHF/person before 17:00 · 999 CHF/person from 17:00 & weekends',
                            '799 CHF/Person vor 17:00 · 999 CHF/Person ab 17:00 & Wochenende')}
                    </p>
                  </div>
                )}
                {form.lesson_type === 'junior_training' && (
                  <p className="text-xs text-slate-700 bg-slate-100 border border-slate-300 rounded-md px-2 py-1">
                    {L('Junior training · covered by Abo · shown gray on the calendar · no price required',
                       'Junior-Training · durch Abo abgedeckt · grau im Kalender · kein Preis nötig')}
                  </p>
                )}
              </div>

              {/* Clients — multi-select. Hidden for Junior trainings — the
                  juniors themselves are the participants. */}
              {form.lesson_type !== 'junior_training' && (
              <div className="space-y-2">
                <Label className="label-text">
                  {L('Clients', 'Kunden')} *
                  {form.selected_clients && form.selected_clients.length > 0 && (
                    <span className="ml-2 text-emerald-600 font-normal">
                      ({form.selected_clients.length} {L('selected', 'ausgewählt')})
                    </span>
                  )}
                </Label>
                {/* Selected client chips */}
                {form.selected_clients && form.selected_clients.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="selected-clients">
                    {form.selected_clients.map(c => (
                      <span key={c.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
                        {c.name}
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, selected_clients: f.selected_clients.filter(x => x.id !== c.id) }))}
                          className="rounded-full hover:bg-emerald-200 p-0.5"
                          data-testid={`remove-client-${c.id}`}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Input
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      if (e.target.value.length < 2) setShowClientDropdown(false);
                    }}
                    onFocus={() => { if (clientResults.length > 0) setShowClientDropdown(true); }}
                    className="bg-slate-50 border-slate-200"
                    placeholder={L('Search by name, email or phone…', 'Name, E-Mail oder Telefon suchen…')}
                    data-testid="lesson-client"
                  />
                  {clientSearching && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">...</span>}
                  {showClientDropdown && clientResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto" data-testid="client-dropdown">
                      {clientResults
                        .filter(u => !(form.selected_clients || []).some(sc => sc.id === u.id))
                        .map(u => (
                          <button key={u.id} onClick={() => {
                            setForm(f => ({
                              ...f,
                              selected_clients: [...(f.selected_clients || []), { id: u.id, name: u.name, email: u.email }],
                              client_id: (f.selected_clients?.length || 0) === 0 ? u.id : f.client_id,
                            }));
                            setClientSearch('');
                            setShowClientDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm transition-colors border-b border-slate-50 last:border-0"
                          data-testid={`client-option-${u.id}`}>
                            <span className="font-medium text-slate-800">{u.name}</span>
                            <span className="text-slate-400 ml-2 text-xs">{u.email}</span>
                          </button>
                        ))}
                    </div>
                  )}
                  {showClientDropdown && clientSearch.length >= 2 && clientResults.length === 0 && !clientSearching && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-center text-sm text-slate-400">
                      {L('No clients found', 'Keine Kunden gefunden')}
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Duration & Participants */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="label-text">{L('Duration', 'Dauer')} <span className="text-slate-400 font-normal">(min)</span></Label>
                  {/* Flexible duration: step 15, from 30 to 240 min. Presets
                      below for the common cases (60/90/120); any value in
                      between (or above 2h) is typed directly. */}
                  <div className="flex items-center gap-1.5">
                    <button type="button"
                            onClick={() => setForm(f => ({ ...f, duration: String(Math.max(30, (parseInt(f.duration) || 60) - 15)) }))}
                            className="h-9 w-9 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold"
                            data-testid="duration-dec">−</button>
                    <input
                      type="number"
                      min="30"
                      max="240"
                      step="15"
                      value={form.duration}
                      onChange={(e) => setForm(f => ({ ...f, duration: e.target.value || '60' }))}
                      className="flex-1 h-9 text-center bg-slate-50 border border-slate-200 rounded-md text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="lesson-duration"
                    />
                    <button type="button"
                            onClick={() => setForm(f => ({ ...f, duration: String(Math.min(240, (parseInt(f.duration) || 60) + 15)) }))}
                            className="h-9 w-9 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold"
                            data-testid="duration-inc">+</button>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {[60, 90, 120, 150, 180].map(preset => (
                      <button key={preset} type="button"
                              onClick={() => setForm(f => ({ ...f, duration: String(preset) }))}
                              className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
                                String(preset) === String(form.duration)
                                  ? 'bg-emerald-500 text-white border-emerald-600'
                                  : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                              }`}
                              data-testid={`duration-preset-${preset}`}>
                        {preset} min
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="label-text">{L('Participants', 'Teilnehmer')}</Label>
                  <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm font-semibold text-slate-700" data-testid="lesson-participants-derived">
                    {(form.selected_clients?.length || 0)} {L((form.selected_clients?.length || 0) === 1 ? 'player' : 'players', (form.selected_clients?.length || 0) === 1 ? 'Spieler' : 'Spieler')}
                  </div>
                  <p className="text-[10px] text-slate-400">{L('Auto from selected clients', 'Automatisch aus Kunden')}</p>
                </div>
              </div>

              {/* Price — auto from matrix, with optional override. Hidden for
                  Junior trainings (0 CHF, covered by Abo). */}
              {form.lesson_type !== 'junior_training' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="label-text">{L('Price', 'Preis')}</Label>
                  <button
                    type="button"
                    onClick={() => { setPriceOverride(p => !p); }}
                    className="text-xs font-semibold text-emerald-600 hover:underline"
                    data-testid="lesson-price-override-toggle"
                  >
                    {priceOverride
                      ? L('← Use official price', '← Offizieller Preis')
                      : L('Override manually', 'Manuell überschreiben')}
                  </button>
                </div>
                {priceOverride ? (
                  <Input type="number" step="5" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))}
                    className="bg-slate-50 border-slate-200" placeholder="100" data-testid="lesson-price" />
                ) : priceLoading ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5 text-sm text-slate-400 italic" data-testid="lesson-price-loading">
                    {L('Calculating price…', 'Preis wird berechnet…')}
                  </div>
                ) : autoPrice ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="lesson-price-auto">
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                        {autoPrice.tier === 'peak' ? L('Peak rate', 'Peak-Tarif') : L('Off-peak rate', 'Off-Peak-Tarif')}
                      </span>
                      <span className="text-xs text-emerald-600">
                        {autoPrice.tier === 'peak'
                          ? L('after 17:00 or weekend', 'nach 17:00 oder Wochenende')
                          : L('Mo–Fr before 17:00', 'Mo–Fr vor 17:00')}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between mt-1">
                      <span className="text-2xl font-extrabold text-emerald-700">
                        {autoPrice.total_price.toFixed(0)} CHF
                      </span>
                      <span className="text-sm text-emerald-600">
                        {autoPrice.price_per_person.toFixed(0)} CHF × {autoPrice.participants}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {L('Auto-priced from padelarena.ch', 'Automatisch von padelarena.ch')}
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-sm text-amber-700" data-testid="lesson-price-fallback">
                    {L('No official price for this combination. Please override manually.', 'Kein offizieller Preis für diese Kombination. Bitte manuell eingeben.')}
                  </div>
                )}
              </div>
              )}

              {/* Abo — one selector per client. Each client can pick their
                  own training abo (CHF-balance model). Multiple selected = each
                  abo pays that client's share of the lesson price. */}
              {form.lesson_type !== 'junior_training' && (form.selected_clients?.length > 0) && (() => {
                const anyAbo = Object.values(clientSubscriptions).some(list => (list || []).length > 0)
                  || Object.values(clientVoucherMap).some(list => (list || []).length > 0);
                if (!anyAbo) return null;
                const totalPrice = Number(form.price) || Number(autoPrice?.total_price) || 0;
                const numClients = Math.max(1, (form.selected_clients || []).length);
                const perPerson = totalPrice / numClients;
                const isRecurring = !!recurringEndDate;
                // Approximate number of weekly sessions if recurring
                let numSessions = 1;
                if (isRecurring && recurringEndDate && selectedSlot?.date) {
                  const start = new Date(format(selectedSlot.date, 'yyyy-MM-dd') + 'T00:00:00');
                  const end = new Date(`${recurringEndDate}T00:00:00`);
                  if (end >= start) numSessions = Math.floor((end - start) / (7 * 86400000)) + 1;
                }
                const round2 = (n) => Math.round(n * 100) / 100;
                const anySelected = Object.values(form.subscription_assignments || {}).some(Boolean)
                  || Object.values(form.voucher_assignments || {}).some(Boolean);
                return (
                  <div className="space-y-2">
                    <Label className="label-text">
                      {L('Deduct from Abo / Voucher', 'Von Abo / Gutschein abziehen')}
                      {isRecurring && numSessions > 1 && (
                        <span className="ml-2 text-[11px] font-semibold text-blue-600">
                          {L(`× ${numSessions} sessions`, `× ${numSessions} Sessions`)}
                        </span>
                      )}
                    </Label>
                    <div className="space-y-2">
                      {(form.selected_clients || []).map(client => {
                        const subs = clientSubscriptions[client.id] || [];
                        const vouchers = clientVoucherMap[client.id] || [];
                        const currentSid = form.subscription_assignments?.[client.id] || '';
                        const currentVid = form.voucher_assignments?.[client.id] || '';
                        if (subs.length === 0 && vouchers.length === 0) {
                          return (
                            <div key={client.id} className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs" data-testid={`abo-client-row-${client.id}`}>
                              <span className="font-semibold text-slate-700 flex-shrink-0">{client.name}</span>
                              <span className="ml-auto text-slate-400 italic">{L('No abo/voucher — pays cash/card', 'Kein Abo/Gutschein — Bar/Karte')}</span>
                            </div>
                          );
                        }
                        const selectedVoucher = vouchers.find(x => x.id === currentVid);
                        const selectedSub = subs.find(x => x.id === currentSid) || (selectedVoucher ? { balance_remaining: selectedVoucher.remaining_balance } : null);
                        const rem = Number(selectedSub?.balance_remaining || 0);
                        const requiredForClient = round2(perPerson * numSessions);
                        const enough = !selectedSub || rem + 0.01 >= requiredForClient;
                        return (
                          <div key={client.id} className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg space-y-2" data-testid={`abo-client-row-${client.id}`}>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-800 text-sm flex-shrink-0 min-w-0 truncate">{client.name}</span>
                              <Select
                                value={currentSid || (currentVid ? `v:${currentVid}` : 'none')}
                                onValueChange={(v) => setForm(f => ({
                                  ...f,
                                  // per client: either one abo OR one voucher
                                  subscription_assignments: { ...(f.subscription_assignments || {}), [client.id]: v === 'none' || v.startsWith('v:') ? '' : v },
                                  voucher_assignments: { ...(f.voucher_assignments || {}), [client.id]: v.startsWith('v:') ? v.slice(2) : '' },
                                  voucher_id: '',
                                }))}
                              >
                                <SelectTrigger className="bg-white border-slate-200 text-sm h-9 flex-1 min-w-0" data-testid={`abo-client-select-${client.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                  <SelectItem value="none">{L('Cash/card', 'Bar/Karte')}</SelectItem>
                                  {subs.map(s => (
                                    <SelectItem key={s.id} value={s.id}>
                                      Abo · {Number(s.balance_remaining || 0).toFixed(0)}/{Number(s.balance_total || 0).toFixed(0)} CHF
                                    </SelectItem>
                                  ))}
                                  {vouchers.map(v => (
                                    <SelectItem key={v.id} value={`v:${v.id}`} data-testid={`voucher-option-${v.id}`}>
                                      {L('Voucher', 'Gutschein')} {v.code} · {Number(v.remaining_balance || 0).toFixed(0)}/{Number(v.amount || 0).toFixed(0)} CHF
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {selectedSub && (
                              <div className={`text-xs rounded-md px-2 py-1.5 ${enough ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                {enough ? (
                                  <>{rem.toFixed(2)} CHF → -{requiredForClient.toFixed(2)} = <strong>{(rem - requiredForClient).toFixed(2)} CHF</strong></>
                                ) : (
                                  <><Warning size={12} className="inline mr-1" /> {L(`Not enough (${rem.toFixed(2)} < ${requiredForClient.toFixed(2)} CHF for ${numSessions} sessions)`, `Reicht nicht (${rem.toFixed(2)} < ${requiredForClient.toFixed(2)} CHF für ${numSessions} Sessions)`)}</>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {anySelected && (
                      <p className="text-[11px] text-emerald-700 pt-1">
                        {L('Clients paying via abo or voucher have their share deducted per session. Others pay cash/card at reception.', 'Kunden mit Abo oder Gutschein bekommen ihren Anteil pro Session abgezogen. Andere zahlen am Empfang.')}
                      </p>
                    )}
                  </div>
                );
              })()}

              {(form.selected_clients?.length > 0) && Object.values(clientVoucherMap).every(list => (list || []).length === 0) && Object.values(clientSubscriptions).every(list => (list || []).length === 0) && (
                <p className="text-amber-600 text-sm bg-amber-50 p-3 rounded-xl">{L('No vouchers or abos for the selected clients', 'Keine Gutscheine oder Abos für die ausgewählten Kunden')}</p>
              )}

              {/* Junior Selection */}
              <div className="space-y-2 border-t border-slate-100 pt-3">
                <div className="flex items-center justify-between">
                  <Label className="label-text flex items-center gap-1.5"><Student size={16} className="text-emerald-600" />{L('Juniors', 'Junioren')}</Label>
                  {selectedJuniors.length > 0 && <span className="text-xs text-emerald-600 font-bold">{selectedJuniors.length} {L('selected', 'ausgewählt')}</span>}
                </div>
                {selectedJuniors.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {selectedJuniors.map(jid => {
                      const j = juniors.find(x => x.id === jid);
                      return j ? (
                        <span key={jid} className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">
                          {j.name}
                          <button onClick={() => setSelectedJuniors(prev => prev.filter(id => id !== jid))} className="text-emerald-400 hover:text-red-500"><X size={12} /></button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
                <Input value={juniorSearch} onChange={(e) => setJuniorSearch(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-sm" placeholder={L('Search juniors...', 'Junioren suchen...')} data-testid="lesson-junior-search" />
                <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-xl">
                  {juniors.filter(j => !juniorSearch || j.name.toLowerCase().includes(juniorSearch.toLowerCase())).map(j => {
                    const selected = selectedJuniors.includes(j.id);
                    // Ignore legacy notes that equal the training_times string
                    const hasRealNotes = !!(j.notes && !(j.notes === j.training_times && /[0-9]{1,2}:[0-9]{2}/.test(j.notes)));
                    return (
                      <div key={j.id}
                        className={`w-full flex items-center border-b border-slate-50 last:border-0 transition-all ${selected ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-slate-50 text-slate-600'}`}>
                        <button onClick={() => setSelectedJuniors(prev => prev.includes(j.id) ? prev.filter(id => id !== j.id) : [...prev, j.id])}
                          className={`flex-1 flex items-center justify-between px-3 py-1.5 text-sm ${selected ? 'font-semibold' : ''}`}
                          data-testid={`lesson-junior-${j.id}`}>
                          <span className="truncate">
                            {j.name}{' '}
                            {j.birth_year ? <span className="text-slate-400 text-xs">({j.birth_year})</span> : ''}
                          </span>
                          {selected && <span className="text-emerald-500 text-xs font-bold">&#10003;</span>}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openJuniorNotes(j); }}
                          className={`px-2 py-1.5 transition-colors ${hasRealNotes ? 'text-emerald-600 hover:text-emerald-800' : 'text-slate-300 hover:text-slate-500'}`}
                          title={hasRealNotes ? L('View / edit notes', 'Notizen ansehen / bearbeiten') : L('Add notes', 'Notiz hinzufügen')}
                          data-testid={`junior-notes-btn-${j.id}`}
                        >
                          <NotePencil size={14} weight={hasRealNotes ? 'fill' : 'regular'} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recurring Toggle */}
              <div className="space-y-2 border-t border-slate-100 pt-3">
                <div className="flex items-center gap-3">
                  <button onClick={() => setIsRecurring(!isRecurring)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${isRecurring ? 'bg-blue-500' : 'bg-slate-300'}`}
                    data-testid="lesson-recurring-toggle">
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isRecurring ? 'translate-x-5' : ''}`} />
                  </button>
                  <span className="text-sm font-medium text-slate-700">{L('Weekly Recurring', 'Wöchentlich wiederkehrend')}</span>
                </div>
                {isRecurring && selectedSlot && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <ArrowsClockwise size={16} className="text-blue-600" />
                      <span className="text-blue-700 font-medium">
                        {L('Every', 'Jeden')} {format(selectedSlot.date, 'EEEE', { locale: language === 'de' ? de : enUS })} {L('at', 'um')} {selectedSlot.time}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-blue-600 font-medium">{L('Until (end date)', 'Bis (Enddatum)')}</Label>
                      <Input type="date" value={recurringEndDate} onChange={(e) => setRecurringEndDate(e.target.value)}
                        min={format(selectedSlot.date, 'yyyy-MM-dd')}
                        className="bg-white border-blue-200 text-slate-900" data-testid="lesson-recurring-end" />
                    </div>
                    {recurringEndDate && (() => {
                      const start = selectedSlot.date;
                      const end = new Date(recurringEndDate);
                      const weeks = Math.max(1, Math.round((end - start) / (7 * 24 * 60 * 60 * 1000)));
                      return <p className="text-xs text-blue-600">≈ {weeks} {L('lessons', 'Lektionen')}</p>;
                    })()}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="label-text">{L('Notes', 'Notizen')}</Label>
                <Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="bg-slate-50 border-slate-200" placeholder={L('Optional notes', 'Optionale Notizen')} data-testid="lesson-notes" />
              </div>

              {/* Info: Court is auto-assigned */}
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl text-sm text-blue-700">
                {L('A court will be automatically assigned.', 'Ein Platz wird automatisch zugewiesen.')}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
                <Button
                  onClick={handleBook}
                  disabled={
                    bookingLoading ||
                    (form.lesson_type === 'junior_training'
                      ? selectedJuniors.length === 0
                      : !(form.selected_clients?.length > 0) || !form.price) ||
                    (isRecurring && !recurringEndDate)
                  }
                  className={`flex-1 font-bold text-white ${isRecurring ? 'bg-blue-500 hover:bg-blue-600' : 'bg-emerald-500 hover:bg-emerald-600'}`} data-testid="confirm-lesson">
                  {bookingLoading ? '...' : isRecurring ? L('Book Series', 'Serie buchen') : L('Book Lesson', 'Lektion buchen')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ LESSON DETAIL / EDIT MODAL ============ */}
      <Dialog open={showDetail} onOpenChange={(open) => { if (!open) { setShowDetail(false); setIsEditing(false); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lesson-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {isEditing ? L('Edit Lesson', 'Lektion bearbeiten') : L('Lesson Details', 'Lektionsdetails')}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {isEditing ? L('Change trainer, time, or details', 'Trainer, Zeit oder Details ändern') : L('View and manage this lesson', 'Lektion anzeigen und verwalten')}
            </DialogDescription>
          </DialogHeader>

          {detailLesson && detailLesson.booking_type === 'trainer_block' && (
            <div className="space-y-3 mt-2" data-testid="trainer-block-detail">
              <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-4 space-y-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-white bg-slate-600 px-2 py-0.5 rounded">{L('Blocked', 'Blockiert')}</span>
                <p className="font-bold text-slate-900 whitespace-pre-line" data-testid="trainer-block-notes">{detailLesson.notes}</p>
                <p className="text-sm text-slate-600">{detailLesson.date} · {detailLesson.start_time}–{detailLesson.end_time}</p>
                {detailLesson.created_by_name && <p className="text-xs text-slate-500">{L('Entered by', 'Eingetragen von')} {detailLesson.created_by_name}</p>}
                <p className="text-[11px] text-slate-500 italic">{L('No court reserved. No lessons can be booked in this time.', 'Kein Platz reserviert. In dieser Zeit können keine Lektionen gebucht werden.')}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDetail(false)} className="flex-1">{L('Close', 'Schliessen')}</Button>
                {canBlockTime && <Button onClick={async () => {
                  if (!window.confirm(L('Remove this block?', 'Blockierung entfernen?'))) return;
                  try { await API.delete(`/trainer/blocks/${detailLesson.block_id}`); toast.success(L('Block removed', 'Blockierung entfernt')); setShowDetail(false); loadSchedule(); }
                  catch (e) { toast.error(e.response?.data?.detail || L('Failed', 'Fehlgeschlagen')); }
                }} variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50" data-testid="trainer-block-delete-btn">
                  {L('Remove block', 'Blockierung entfernen')}
                </Button>}
              </div>
            </div>
          )}

          {/* VIEW MODE — GROUP TRAINING (read-only, no edit) */}
          {detailLesson && !isEditing && detailLesson.booking_type === 'group_training' && (
            <div className="space-y-3 mt-2">
              <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-wider text-white bg-emerald-600 px-2 py-0.5 rounded">
                    {L('Group Training', 'Gruppen-Training')}
                  </span>
                </div>
                <h4 className="font-bold text-slate-900 text-base">{detailLesson.notes || L('Training', 'Training')}</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  <div>📅 {detailLesson.date} · {detailLesson.start_time} · {detailLesson.duration_minutes} min</div>
                  <div>👥 {detailLesson.participant_count || 0} / {detailLesson.max_participants || 0} {L('participants', 'Teilnehmer')}</div>
                </div>
                <GroupTrainingParticipants programId={detailLesson.training_program_id} API={API} L={L}
                  canManage={['admin', 'staff'].includes(user?.role)} onNavigate={() => setShowDetail(false)} />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowDetail(false)}
                className="w-full"
                data-testid="close-group-lesson-btn"
              >
                {L('Close', 'Schließen')}
              </Button>
            </div>
          )}

          {/* VIEW MODE — REGULAR 1-on-1 LESSON */}
          {detailLesson && !isEditing && !['group_training', 'trainer_block'].includes(detailLesson.booking_type) && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3">
                {!detailLesson.academy_group_id && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">{L('Client', 'Kunde')}</span>
                    <span className="text-slate-900 font-bold" data-testid="lesson-detail-clients">{(detailLesson.client_names || []).length > 1 ? detailLesson.client_names.join(', ') : detailLesson.user_name}</span>
                  </div>
                )}
                {detailLesson.abo_sessions && (
                  <div className="flex justify-between items-center" data-testid="detail-abo-sessions-row">
                    <span className="text-slate-500">Abo</span>
                    <AboSessionBadges sessions={detailLesson.abo_sessions} size="sm" className="justify-end" testPrefix="detail-abo-session" />
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">{L('Trainer', 'Trainer')}</span>
                  <span className="text-emerald-700 font-bold">{detailLesson.trainer_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">{L('Court', 'Platz')}</span>
                  <span className="text-slate-900">{detailLesson.court_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">{L('Date', 'Datum')}</span>
                  <span className="text-slate-900">{detailLesson.date}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">{L('Time', 'Zeit')}</span>
                  <span className="text-slate-900 font-semibold">{detailLesson.start_time} - {detailLesson.end_time}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">{L('Duration', 'Dauer')}</span>
                  <span className="text-slate-900">{detailLesson.duration_minutes} min</span>
                </div>
                {(detailLesson.booked_by_name || detailLesson.created_at) && (
                  <div className="flex justify-between items-start gap-3 border-t border-slate-200 pt-3">
                    <span className="text-slate-500">{L('Booked', 'Gebucht')}</span>
                    <div className="text-right">
                      {detailLesson.created_at && (
                        <div className="text-slate-900 font-semibold text-sm" data-testid="lesson-created-at">
                          {new Date(detailLesson.created_at).toLocaleString(language === 'de' ? 'de-CH' : 'en-GB', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </div>
                      )}
                      {detailLesson.booked_by_name && (
                        <div className="text-xs text-slate-500 mt-0.5" data-testid="lesson-booked-by">
                          {L('by', 'von')} <span className="font-medium text-slate-700">{detailLesson.booked_by_name}</span>
                          {detailLesson.booked_by_role && (
                            <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                              detailLesson.booked_by_role === 'admin' ? 'bg-red-100 text-red-700' :
                              detailLesson.booked_by_role === 'staff' ? 'bg-amber-100 text-amber-700' :
                              detailLesson.booked_by_role === 'trainer' ? 'bg-purple-100 text-purple-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {detailLesson.booked_by_role === 'admin' ? L('Admin', 'Admin') :
                               detailLesson.booked_by_role === 'staff' ? L('Reception', 'Empfang') :
                               detailLesson.booked_by_role === 'trainer' ? L('Trainer', 'Trainer') :
                               L('Customer', 'Kunde')}
                            </span>
                          )}
                          {detailLesson.booked_by_id && detailLesson.user_id &&
                           detailLesson.booked_by_id !== detailLesson.user_id &&
                           detailLesson.booked_by_role !== 'customer' && (
                            <span className="ml-1 text-[10px] text-amber-700 font-semibold">
                              {L('(for client)', '(für Kunden)')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {detailLesson.voucher_id && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">{L('Voucher', 'Gutschein')}</span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                      {L('Deducted', 'Abgezogen')}: {detailLesson.voucher_deducted?.toFixed(0) || detailLesson.amount?.toFixed(0)} CHF
                    </span>
                  </div>
                )}
                {(detailLesson.paid_via_vouchers || []).map(pv => (
                  <div key={pv.voucher_id} className="flex justify-between items-center" data-testid={`lesson-voucher-${pv.client_id}`}>
                    <span className="text-slate-500">{L('Voucher', 'Gutschein')} · {pv.client_name}</span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                      {pv.voucher_code} · {L('Deducted', 'Abgezogen')}: {Number(pv.amount_deducted).toFixed(2)} CHF
                    </span>
                  </div>
                ))}

                <div className="flex justify-between items-center border-t border-slate-200 pt-3">
                  <span className="text-slate-900 font-semibold">{L('Price', 'Preis')}</span>
                  <span className="text-xl font-extrabold text-emerald-600">{(detailLesson.amount || 0).toFixed(0)} CHF</span>
                </div>
                {detailLesson.notes && (
                  <div className="border-t border-slate-200 pt-3">
                    <span className="text-slate-500 text-sm">{L('Notes', 'Notizen')}: </span>
                    <span className="text-slate-700 text-sm">{detailLesson.notes}</span>
                  </div>
                )}
                {detailLesson.academy_group_id && (
                  <AcademyJuniorChips booking={detailLesson} API={API} L={L}
                    onChange={(patch) => { setDetailLesson(prev => prev ? { ...prev, ...patch } : prev); loadSchedule(); }} />
                )}
                {!detailLesson.academy_group_id && detailLesson.junior_names?.length > 0 && (
                  <div className="border-t border-slate-200 pt-3">
                    <span className="text-slate-500 text-sm block mb-1">{L('Juniors', 'Junioren')}:</span>
                    <div className="flex flex-wrap gap-1">
                      {detailLesson.junior_names.map((name, i) => (
                        <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">{name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {detailLesson.lesson_type === 'adult_academy' && (
                <div data-testid="trainer-detail-adult-academy">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{L('Adult Academy · participants & payments', 'Adult Academy · Teilnehmer & Zahlungen')}</p>
                  <AdultAcademyPanel API={API} L={L} bookingId={detailLesson.id} />
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button onClick={startEditLesson} className="flex-1 bg-slate-800 text-white hover:bg-slate-700 font-semibold" data-testid="edit-lesson-btn">
                    <PencilSimple size={16} className="mr-2" /> {L('Edit', 'Bearbeiten')}
                  </Button>
                  <Button onClick={() => setShowDeleteConfirm(true)} variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50 font-semibold" data-testid="delete-lesson-btn">
                    <Trash size={16} className="mr-2" /> {L('Delete', 'Löschen')}
                  </Button>
                </div>

                {showDeleteConfirm && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                    {(detailLesson.recurring_group_id || detailLesson.recurring_id) ? (
                      <>
                        <p className="text-sm text-red-700 font-medium">
                          {L('This lesson is part of a recurring series. What do you want to delete?',
                             'Diese Lektion gehört zu einer Serie. Was möchten Sie löschen?')}
                        </p>
                        <div className="flex flex-col gap-2">
                          <Button onClick={handleDeleteLesson} variant="outline"
                            className="w-full border-amber-400 text-amber-800 hover:bg-amber-100 font-semibold justify-start bg-white"
                            data-testid="delete-single-lesson-btn">
                            <X size={16} className="mr-2" />
                            {L('Only this lesson', 'Nur diese Lektion')}
                          </Button>
                          <Button onClick={handleDeleteSeries}
                            className="w-full bg-red-600 text-white hover:bg-red-700 font-semibold justify-start"
                            data-testid="delete-lesson-series-btn">
                            <Trash size={16} className="mr-2" />
                            {L('This & all future lessons in the series', 'Diese & alle zukünftigen Lektionen der Serie')}
                          </Button>
                        </div>
                        <Button onClick={() => setShowDeleteConfirm(false)} variant="outline"
                          className="w-full border-slate-200 text-slate-600 bg-white text-xs">
                          {L('Cancel', 'Abbrechen')}
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-red-700 font-medium">
                          {detailLesson.voucher_id
                            ? L('Delete this lesson? The voucher amount will be refunded.', 'Diese Lektion löschen? Der Gutscheinbetrag wird zurückerstattet.')
                            : L('Are you sure you want to delete this lesson?', 'Sind Sie sicher, dass Sie diese Lektion löschen möchten?')}
                        </p>
                        <div className="flex gap-2">
                          <Button onClick={() => setShowDeleteConfirm(false)} variant="outline" className="flex-1 border-slate-200 text-slate-600 bg-white">
                            {L('Cancel', 'Abbrechen')}
                          </Button>
                          <Button onClick={handleDeleteLesson} className="flex-1 bg-red-600 text-white hover:bg-red-700 font-bold" data-testid="confirm-delete-lesson">
                            <Trash size={16} className="mr-2" /> {L('Yes, delete', 'Ja, löschen')}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {detailLesson.voucher_id && !showDeleteConfirm && (
                <p className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-xl">
                  {L('Deleting this lesson will refund the voucher amount.', 'Beim Löschen wird der Gutscheinbetrag zurückerstattet.')}
                </p>
              )}
            </div>
          )}

          {/* EDIT MODE */}
          {detailLesson && isEditing && (
            <div className="space-y-4 mt-2">
              {canEditClients && editForm.clients && (
                <LessonClientsEditor API={API} L={L} clients={editForm.clients}
                  deskPaid={Math.max(0, (detailLesson.paid_participants || 0) - (detailLesson.paid_via_subscriptions || []).length - (detailLesson.paid_via_vouchers || []).length)}
                  onChange={(clients) => setEditForm(f => ({ ...f, clients }))} />
              )}
              {/* Trainer */}
              <div className="space-y-2">
                <Label className="label-text">{L('Trainer', 'Trainer')}</Label>
                <Select value={editForm.trainer_id} onValueChange={(v) => setEditForm(f => ({ ...f, trainer_id: v }))}>
                  <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="edit-lesson-trainer"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {displayTrainers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Start Time */}
              <div className="space-y-2">
                <Label className="label-text flex items-center gap-1.5"><Clock size={14} /> {L('Start Time', 'Startzeit')}</Label>
                <Select value={editForm.start_time} onValueChange={(v) => setEditForm(f => ({ ...f, start_time: v }))}>
                  <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="edit-lesson-time"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 max-h-48">
                    {timeSlots.map(ts => <SelectItem key={ts} value={ts}>{ts}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label className="label-text">{L('Duration', 'Dauer')} <span className="text-slate-400 font-normal">(min)</span></Label>
                <div className="flex items-center gap-1.5">
                  <button type="button"
                          onClick={() => setEditForm(f => ({ ...f, duration_minutes: String(Math.max(30, (parseInt(f.duration_minutes) || 60) - 15)) }))}
                          className="h-9 w-9 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold">−</button>
                  <input
                    type="number" min="30" max="240" step="15"
                    value={editForm.duration_minutes}
                    onChange={(e) => setEditForm(f => ({ ...f, duration_minutes: e.target.value || '60' }))}
                    className="flex-1 h-9 text-center bg-slate-50 border border-slate-200 rounded-md text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="edit-lesson-duration"
                  />
                  <button type="button"
                          onClick={() => setEditForm(f => ({ ...f, duration_minutes: String(Math.min(240, (parseInt(f.duration_minutes) || 60) + 15)) }))}
                          className="h-9 w-9 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold">+</button>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {[30, 60, 90, 120, 150, 180].map(preset => (
                    <button key={preset} type="button"
                            onClick={() => setEditForm(f => ({ ...f, duration_minutes: String(preset) }))}
                            className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
                              String(preset) === String(editForm.duration_minutes)
                                ? 'bg-emerald-500 text-white border-emerald-600'
                                : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                            }`}>
                      {preset} min
                    </button>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div className="space-y-2">
                <Label className="label-text">{L('Price (CHF)', 'Preis (CHF)')}
                  {editForm.clients && <span className="ml-2 text-slate-400 font-normal" data-testid="edit-lesson-price-per-person">
                    ({(parseFloat(editForm.lesson_price || 0) / Math.max(1, editForm.clients.length)).toFixed(2)} CHF × {editForm.clients.length})
                  </span>}
                </Label>
                <Input type="number" step="10" value={editForm.lesson_price} onChange={(e) => setEditForm(f => ({ ...f, lesson_price: e.target.value }))}
                  className="bg-slate-50 border-slate-200" data-testid="edit-lesson-price" />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="label-text">{L('Notes', 'Notizen')}</Label>
                <Input value={editForm.notes} onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  className="bg-slate-50 border-slate-200" data-testid="edit-lesson-notes" />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsEditing(false)} className="flex-1 border-slate-200 text-slate-600">
                  {L('Cancel', 'Abbrechen')}
                </Button>
                <Button onClick={handleSaveLesson} disabled={editLoading || editPriceLoading}
                  className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="save-lesson-btn">
                  {editLoading ? '...' : L('Save Changes', 'Änderungen speichern')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Junior Notes editor */}
      <Dialog open={!!noteJunior} onOpenChange={(open) => !open && setNoteJunior(null)}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="junior-notes-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <NotePencil size={20} className="text-emerald-600" />
              {noteJunior?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              {L('Shared coaching notes — visible to all coaches, admin and reception.',
                 'Gemeinsame Coaching-Notizen — sichtbar für alle Coaches, Admin und Empfang.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              rows={6}
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-emerald-200"
              placeholder={L('Progress, injuries, special needs…', 'Fortschritt, Verletzungen, Besonderheiten…')}
              data-testid="junior-notes-textarea"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setNoteJunior(null)} className="border-slate-200 text-slate-600" data-testid="junior-notes-cancel">
                {L('Cancel', 'Abbrechen')}
              </Button>
              <Button onClick={saveJuniorNotes} disabled={noteSaving} className="bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="junior-notes-save">
                {noteSaving ? '...' : L('Save', 'Speichern')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainerSchedule;
